import {
  FunctionComponent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import useStyles from './styles'
import { TabProps } from './types'
import { useValidateProps } from './utils'

import { useTabsContext } from '@/components/Tabs/TabsContext'

export const Tab: FunctionComponent<TabProps> = ({
  styles: overrides,
  size: sizeProp = 'md',
  label,
  value,
  active: activeProp = false,
  onClick,
  ...props
}) => {
  const { size } = useValidateProps({ sizeProp })
  const tabsContext = useTabsContext()
  const tabRef = useRef<HTMLButtonElement>()

  const active = useMemo(() => {
    if (tabsContext?.value !== undefined) {
      return String(tabsContext?.value) === String(value)
    }
    return activeProp
  }, [tabsContext?.value, activeProp, value])

  useEffect(() => {
    if (active && tabsContext?.value !== undefined) {
      const tab = tabRef.current
      const scroller = tabsContext.scroller.current
      scroller.scroll({
        behavior: 'smooth',
        left: tab.offsetLeft - scroller.clientWidth / 2 + tab.clientWidth / 2,
      })
    }
  }, [active, tabsContext?.value, tabsContext?.scroller])

  const styles = useStyles({ active, size })

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    event => {
      onClick && onClick(event, value)
      tabsContext?.onChange(value)
    },
    [onClick, tabsContext, value],
  )

  return (
    <button
      data-testid="hero-tab"
      {...props}
      value={value}
      css={[styles.root, overrides?.root]}
      onClick={handleClick}
      ref={tabRef}
    >
      <span css={[styles.label, overrides?.label]}>{label}</span>
    </button>
  )
}
